import { Component, Input, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import {
  PointPlanForManage,
  PointPlanRequest,
} from 'src/lib/entity/manage-point'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ConsoleLogger } from 'src/lib/console-logger'
import { ToastrService } from 'ngx-toastr'
import { CommonService } from 'src/lib/services/common-service'
import { timeValidator } from 'src/app/parts/validation/time-validator'

export type Menu = 'point_plan_edit'

@Component({
  selector: 'maglive-admin-point-plan-edit',
  templateUrl: './point-plan-edit.component.html',
  styleUrls: [
    './point-plan-edit.component.scss',
    '../../../../pages/admin/startbootstrap-sb-admin-gh-pages/src/scss/styles.scss',
  ],
})
export class AdminPointPlanEditComponent implements OnInit {
  formGroup: FormGroup
  isShowProductIdInput = false
  isWaiting: boolean

  selectedEditItem: PointPlanForManage

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private logger: ConsoleLogger,
    private commonService: CommonService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.commonService.pointPlan.subscribe((data) => {
      console.log(`data: ${JSON.stringify(data)}`)
      this.selectedEditItem = data
    })
    if (this.selectedEditItem.deviceType !== 'WEB') {
      this.isShowProductIdInput = true
    }
    let numPattern = /^[0-9]+$/
    this.formGroup = this.fb.group(
      {
        name: [
          this.selectedEditItem.name,
          [Validators.required, Validators.maxLength(20)],
        ],
        deviceType: [
          this.selectedEditItem.deviceType,
          [Validators.required, Validators.pattern('WEB|IOS|ANDROID')],
        ],
        description: [
          this.selectedEditItem.description,
          Validators.maxLength(140),
        ],
        amount: [
          this.selectedEditItem.amount,
          [
            Validators.required,
            Validators.min(50),
            Validators.pattern(numPattern),
            Validators.max(100000000),
          ],
        ],
        storeProductCode: [this.selectedEditItem.storeProductCode],
        paidPoint: [
          this.selectedEditItem.paidPoint,
          [
            Validators.required,
            Validators.min(1),
            Validators.pattern(numPattern),
            Validators.max(100000000),
          ],
        ],
        freePoint: [
          this.selectedEditItem.freePoint,
          [
            Validators.required,
            Validators.min(0),
            Validators.pattern(numPattern),
            Validators.max(100000000),
          ],
        ],
        orderBy: [
          this.selectedEditItem.orderBy,
          [
            Validators.required,
            Validators.min(0),
            Validators.pattern(numPattern),
            Validators.max(100000000),
          ],
        ],
        status: [
          this.selectedEditItem.status,
          [Validators.required, Validators.pattern('1|2')],
        ],
        startedAt: [
          this.convertDateFormatForInput(this.selectedEditItem.startedAt),
          Validators.required,
        ],
        endedAt: [
          this.convertDateFormatForInput(this.selectedEditItem.endedAt),
        ],
      },
      { validators: timeValidator }
    )
  }

  get name() {
    return this.formGroup.get('name')
  }
  get deviceType() {
    return this.formGroup.get('deviceType')
  }
  get description() {
    return this.formGroup.get('description')
  }
  get amount() {
    return this.formGroup.get('amount')
  }
  get storeProductCode() {
    return this.formGroup.get('storeProductCode')
  }
  get paidPoint() {
    return this.formGroup.get('paidPoint')
  }
  get freePoint() {
    return this.formGroup.get('freePoint')
  }
  get orderBy() {
    return this.formGroup.get('orderBy')
  }
  get status() {
    return this.formGroup.get('status')
  }
  get startedAt() {
    return this.formGroup.get('startedAt')
  }
  get endedAt() {
    return this.formGroup.get('endedAt')
  }

  /**
   * 選択したポイントプランをアップデートする
   */
  updatePointPlan(): void {
    if (this.isWaiting) return

    this.isWaiting = true

    let request = this.makeInsertParams()
    this.logger.info(
      this.constructor.name,
      'updatePointPlan',
      'request',
      request
    )
    if (request === 'VALIDATION_ERROR') {
      return alert('必須入力項目を再度確認してください。')
    }
    MagliveApiClient.updatePointPlan(
      request,
      this.selectedEditItem.pointPlanId
    ).subscribe(
      (response) => {
        if (response === 'NOT_ADMIN') {
          this.toastr.error('管理者以外は登録できません。')
        } else if (response === 'VALIDATION_ERROR') {
          this.toastr.error('入力項目が正しくありません。')
        } else {
          this.toastr.success('編集が完了しました。')
          this.router.navigate(['../..'], { relativeTo: this.route })
        }
        setTimeout(() => {
          this.isWaiting = false
        }, 3000)
      },
      (error) => {
        console.error(error)
        this.toastr.error('編集に失敗しました。')
        setTimeout(() => {
          this.isWaiting = false
        }, 3000)
      }
    )
  }

  /**
   * 選択したポイントプランを削除する
   */
  deletePointPlan(): void {
    if (confirm('本当に削除しますか？')) {
      MagliveApiClient.deletePointPlan(
        this.selectedEditItem.pointPlanId
      ).subscribe((value) => {
        if (value === null) {
          if (alert('削除しました。') === undefined) {
            this.router.navigate(['../..'], { relativeTo: this.route })
            return
          }
        } else {
          if (alert('削除に失敗しました') === undefined) {
            this.router.navigate(['../..'], { relativeTo: this.route })
          }
        }
      })
    } else {
      return
    }
  }

  /**
   * ポイントプランリクエストのパラメータを作る
   */
  private makeInsertParams(): PointPlanRequest | 'VALIDATION_ERROR' {
    let inputed = this.formGroup.value

    if (
      inputed.name.length === 0 ||
      inputed.amount.length === 0 ||
      inputed.paidPoint.length === 0 ||
      inputed.freePoint.length === 0 ||
      inputed.orderBy.length === 0 ||
      inputed.startedAt.length === 0 ||
      inputed.description?.length >= 140 ||
      (inputed.deviceType !== 'WEB' && inputed.storeProductCode.length === 0)
    ) {
      return 'VALIDATION_ERROR'
    }

    return {
      name: inputed.name,
      description: inputed.description,
      amount: Number(inputed.amount),
      storeProductCode: inputed.storeProductCode,
      deviceType: inputed.deviceType,
      paidPoint: Number(inputed.paidPoint),
      freePoint: Number(inputed.freePoint),
      orderBy: Number(inputed.orderBy),
      startedAt: this.convertUnixTime(inputed.startedAt),
      endedAt: inputed.endedAt ? this.convertUnixTime(inputed.endedAt) : null,
      status: Number(inputed.status),
    }
  }

  /**
   * 文字列で渡された日付をUnixTime(ミリ秒単位)に変換する
   */
  private convertUnixTime(datetimeString): number {
    let date = new Date(datetimeString)
    return date.getTime() / 1000
  }

  /**
   * inputタグ内の初期値表示用にフォーマットする
   */
  private convertDateFormatForInput(str?: string): string {
    if (str == null) return
    str = str.split('/').join('-')
    return str.replace(' ', 'T')
  }

  /**
   * OSをWEB以外に選択した場合はプロダクトID入力欄を非表示にする
   */
  onChangeDeviceType(value: string): void {
    if (value === 'WEB') {
      this.isShowProductIdInput = false
      this.storeProductCode.setValidators(null)
    } else if (value === 'IOS') {
      this.isShowProductIdInput = true
      this.storeProductCode.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9a-zA-Z_.]*$/),
      ])
    } else {
      this.isShowProductIdInput = true
      this.storeProductCode.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9a-z][0-9a-z_.]*$/),
      ])
    }
    this, this.storeProductCode.updateValueAndValidity()
  }
}
